// ShareBrowserModal.js - 공유 프로모션 전용 브라우저 안내 모달
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, ExternalLink } from 'lucide-react';
import { isKakaoInAppBrowser } from '../utils/deviceUtils';

const ShareBrowserModal = ({ isOpen, onClose }) => {
    const [copied, setCopied] = useState(false);
    const isKakao = isKakaoInAppBrowser();
    
    // 기기별 권장 브라우저 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const recommendedBrowser = isIOS ? 'Safari' : 'Chrome';
    const url = 'www.fcfunnel.com';
    const fullUrl = `https://${url}`;

    // 주소 복사 기능
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // 클립보드 API 실패 시 폴백
            const textArea = document.createElement('textarea');
            textArea.value = fullUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // 안드로이드 카카오 인앱 → 크롬으로 열기 (Intent)
    const handleOpenInChrome = () => {
        try {
            // 크롬이 설치되어 있는지 먼저 확인
            const intentUrl = `intent://${url}/#Intent;scheme=https;package=com.android.chrome;end`;

            // 인텐트 시도
            window.location.href = intentUrl;

            // 크롬이 없으면 바로 웹 URL로 이동
            setTimeout(() => {
                window.location.href = fullUrl;
            }, 1000);
        } catch (_) {
            // 최종 폴백: 웹 URL 열기
            window.location.href = fullUrl;
        }
    };

    // iOS Safari로 열기
    const handleOpenInSafari = () => {
        window.location.href = fullUrl;
    };

    if (!isOpen || !isKakao) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 헤더 */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-4xl">⚠️</div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* 내용 */}
                    <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                            브라우저 호환성 안내
                        </h3>
                        <div className="text-gray-700 leading-relaxed">
                            <p className="mb-4 font-medium">
                                앱 공유 프로모션은 <strong className="text-blue-600">
                                    {isIOS ? 'Safari(아이폰)' : 'Chrome(갤럭시)'}
                                </strong> 브라우저에서만 이용 가능합니다.
                            </p>
                            <p className="mb-6">
                                카카오톡 인앱 브라우저에서는 공유 기능이 제한될 수 있습니다.<br />
                                더 나은 경험을 위해 아래 주소를 <strong className="text-blue-600">{recommendedBrowser}</strong>에서 열어주세요.
                            </p>
                        </div>

                        {/* 주소창과 복사 버튼 */}
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-sm font-mono text-gray-800 flex-1 text-left truncate">
                                    {fullUrl}
                                </span>
                                <button
                                    onClick={handleCopyUrl}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${
                                        copied
                                            ? 'bg-green-500 text-white'
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                    title="주소 복사"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            복사됨
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-4 h-4" />
                                            복사
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
                            <div className="text-sm text-green-800">
                                <strong>💡 추가 팁:</strong><br />
                                {recommendedBrowser} 브라우저에서 '홈 화면에 추가'를 하시면 더욱 편리하게 활용 가능합니다.
                            </div>
                        </div>
                    </div>

                    {/* 버튼 */}
                    <div className="space-y-3">
                        {!isIOS ? (
                            // 안드로이드: 크롬으로 열기
                            <button
                                onClick={handleOpenInChrome}
                                className="w-full py-3 px-4 rounded-xl font-bold bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-5 h-5" />
                                Chrome으로 열기
                            </button>
                        ) : (
                            // iOS: Safari로 열기
                            <button
                                onClick={handleOpenInSafari}
                                className="w-full py-3 px-4 rounded-xl font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-5 h-5" />
                                Safari로 열기
                            </button>
                        )}

                        <button
                            onClick={onClose}
                            className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                        >
                            닫기
                        </button>
                    </div>

                    {/* 추가 안내 */}
                    <div className="mt-4 text-center">
                        <p className="text-xs text-gray-500">
                            복사된 주소를 {recommendedBrowser} 브라우저 주소창에<br />
                            직접 입력해주세요.
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ShareBrowserModal;

